import { useEffect, useState } from 'react';
import { Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import Modal from '@/components/DetailsModal';
import SectionView from '@/components/SectionView';
import PropTypes from 'prop-types';
import prepareTranslate from '@/helpers/dictionary';
import { ACCORD_TYPE } from '@/helpers/enums';
import { formatDateToString } from '@/helpers/string';
import DownloadFile from '../DownloadFile';
import TieredCommissionView from '../TieredCommissionView';
import styles from './style.module.css';

const AgentAgreementsView = (props) => {
  const {
    opened, open, close, id, data,
  } = props;

  const t = prepareTranslate();
  const [selectedData, setSelectedData] = useState(null);
  const [archivedData, setArchivedData] = useState([]);
  const [historyOpened, { open: openHistory, close: closeHistory }] = useDisclosure(false);

  const shouldShowValue = (val) => val !== undefined
    && val !== null
    && !(typeof val === 'number' && val === 0)
    && !(typeof val === 'string' && val.trim() === '0');

  const handleBranchString = (branchId) => {
    if (!branchId?.length) return '';
    return branchId.map((branch) => branch.hebrew).join(', ');
  };

  useEffect(() => {
    if (opened && id && data?.length) {
      const found = data.find((item) => item._id === id);
      setSelectedData(found);

      const history = data
        .filter((item) => item.agentNumber === found.agentNumber && item.companyId?._id === found.companyId?._id && item.status === 'archive')
        .sort((a, b) => new Date(b.archiveAt) - new Date(a.archiveAt));

      setArchivedData(history);
    }
  }, [opened, id, data]);

  if (!selectedData) return null;

  const buildLines = (item) => {
    const linesMain = [
      { title: t('components.viewAgentAgreements.labels.agentnumber'), value: item.agentNumber },
      { title: t('components.viewAgentAgreements.labels.branch'), value: handleBranchString(item.branchId) },
      { title: t('components.viewAgentAgreements.labels.accordType'), value: ACCORD_TYPE.find((type) => type.value === item.accordType)?.label },
      shouldShowValue(item.accordName) && { title: t('components.viewAgentAgreements.labels.accordName'), value: item.accordName },
    ].filter(Boolean);

    let detailsDepositCommission = [];
    let detailsAccumulationCommission = [];

    if (item.accordType === ACCORD_TYPE[0].value) {
      detailsDepositCommission = [
        { title: t('components.viewAgentAgreements.labels.date.depositFrom'), value: formatDateToString(item.depositFrom) },
        { title: t('components.viewAgentAgreements.labels.date.depositUntil'), value: formatDateToString(item.depositUntil) },
        shouldShowValue(item.agentFeeMonthlyDeposit) && { title: t('components.viewAgentAgreements.labels.commission.agentFeeMonthlyDeposit'), value: Number(item.agentFeeMonthlyDeposit).toLocaleString('en-US') },
        shouldShowValue(item.agentFeeMonthlyAccumulation) && { title: t('components.viewAgentAgreements.labels.commission.agentFeeMonthlyAccumulation'), value: Number(item.agentFeeMonthlyAccumulation).toLocaleString('en-US') },
      ].filter(Boolean);
    }

    if (item.accordType === ACCORD_TYPE[1].value) {
      detailsDepositCommission = [
        shouldShowValue(item.depositFrom) && { title: t('components.viewAgentAgreements.labels.date.depositFrom'), value: formatDateToString(item.depositFrom) },
        shouldShowValue(item.depositUntil) && { title: t('components.viewAgentAgreements.labels.date.depositUntil'), value: formatDateToString(item.depositUntil) },
        shouldShowValue(item.employeeAgentCommission) && { title: t('components.viewAgentAgreements.labels.commission.agentFee'), value: Number(item.employeeAgentCommission).toLocaleString('en-US') },
      ].filter(Boolean);
    }

    detailsAccumulationCommission = [
      { title: t('components.viewAgentAgreements.labels.date.accumulationFrom'), value: formatDateToString(item.accumulationFrom) },
      { title: t('components.viewAgentAgreements.labels.date.accumulationUntil'), value: formatDateToString(item.accumulationUntil) },
      shouldShowValue(item.comments) && { title: t('components.viewAgentAgreements.labels.comments'), value: item.comments },
    ].filter(Boolean);

    return [linesMain, detailsDepositCommission, detailsAccumulationCommission];
  };

  return (
    <>
      <Modal
        opened={opened}
        close={close}
        open={open}
        label={t('components.viewAgentAgreements.labels.viewAgreement')}
        size='90%'
      >
        {buildLines(selectedData).map((lines, idx) => (
          <SectionView key={idx} lines={lines} />
        ))}
        <TieredCommissionView
          tiers={selectedData.agentFeeAnnualDeposit}
          label={t('components.editContracts.commision.agentFeeAnnualDeposit')}
        />
        <TieredCommissionView
          tiers={selectedData.agentFeeAnnualAccumulation}
          label={t('components.editContracts.commision.agentFeeAnnualAccumulation')}
        />

        {selectedData.files?.map((file) => (
          <DownloadFile key={file.id} file={file} />
        ))}

        {archivedData.length > 0 && (
          <Button mt='md' onClick={openHistory}>
            {t('components.viewAgentAgreements.labels.history')}
          </Button>
        )}
      </Modal>

      <Modal padding='none' opened={historyOpened} close={closeHistory} label={t('components.viewAgentAgreements.labels.history')}>
        {archivedData.map((item, index) => (
          <div
            key={item.id}
            className={`${styles.historyData} ${styles.agreementItem} ${index === archivedData.length - 1 ? styles.lastSection : ''}`}
          >
            {buildLines(item).map((lines, idx) => (
              <SectionView key={idx} lines={lines} />
            ))}
            <TieredCommissionView
              tiers={item.agentFeeAnnualDeposit}
              label={t('components.editContracts.commision.agentFeeAnnualDeposit')}
            />
            <TieredCommissionView
              tiers={item.agentFeeAnnualAccumulation}
              label={t('components.editContracts.commision.agentFeeAnnualAccumulation')}
            />

            {item.files?.map((file) => (
              <DownloadFile key={file.id} file={file} />
            ))}
          </div>
        ))}
      </Modal>
    </>
  );
};

AgentAgreementsView.propTypes = {
  opened: PropTypes.bool,
  open: PropTypes.func,
  close: PropTypes.func,
  id: PropTypes.string,
  data: PropTypes.array,
};

export default AgentAgreementsView;
